import { CellularBillingRow, OverlayBillingRow } from '../../../../shared/model/cellular-billing';

export type BillingGroupKind = 'cellular' | 'overlay';
export type BillingGroupRow = CellularBillingRow | OverlayBillingRow;
export interface BillingMspGroup {
  key: string;
  msp: string;
  rows: BillingGroupRow[];
  cellularRows: CellularBillingRow[];
  overlayRows: OverlayBillingRow[];
}

export function groupBillingRows(rows: BillingGroupRow[], kind: BillingGroupKind): BillingMspGroup[] {
  const groups = new Map<string, BillingMspGroup>();
  rows.forEach(row => {
    const msp = String(kind === 'cellular' ? (row as CellularBillingRow).deviceGroup || '' : (row as OverlayBillingRow).msp || '').trim();
    const key = msp.toLowerCase();
    if (!groups.has(key)) groups.set(key, { key, msp: msp || 'Unassigned MSP', rows: [], cellularRows: [], overlayRows: [] });
    const group = groups.get(key);
    group.rows.push(row);
    if (kind === 'cellular') group.cellularRows.push(row as CellularBillingRow);
    else group.overlayRows.push(row as OverlayBillingRow);
  });
  return Array.from(groups.values()).sort((a, b) => a.msp.localeCompare(b.msp));
}

function shownRows(group: BillingMspGroup, kind: BillingGroupKind, duplicatesOnly: boolean): BillingGroupRow[] {
  return group.rows.filter(row => (kind === 'cellular' ? Number((row as CellularBillingRow).verificationGb) >= .001 : Number((row as OverlayBillingRow).dataUsageGb) > 14)
    && (!duplicatesOnly || row.hasDuplicateMac));
}

export function summarizeBillingGroup(group: BillingMspGroup, kind: BillingGroupKind, duplicatesOnly: boolean): { devices: number, usageGb: number, chargeGb: number | null } {
  const rows = shownRows(group, kind, duplicatesOnly);
  const usageGb = rows.reduce((sum, row) => sum + Number(kind === 'cellular' ? (row as CellularBillingRow).verificationGb : (row as OverlayBillingRow).dataUsageGb), 0);
  const chargeGb = rows.some(row => row.gigsToCharge === null || !Number.isFinite(Number(row.gigsToCharge))) ? null
    : rows.reduce((sum, row) => sum + Number(row.gigsToCharge), 0);
  return { devices: rows.length, usageGb, chargeGb };
}

export function matchesBillingGroup(group: BillingMspGroup, kind: BillingGroupKind, search: string, duplicatesOnly: boolean): boolean {
  const rows = shownRows(group, kind, duplicatesOnly);
  if (!rows.length) return false;
  const term = search.trim().toLowerCase();
  if (!term || group.msp.toLowerCase().includes(term)) return true;
  return rows.some(row => {
    const fields = kind === 'cellular'
      ? [(row as CellularBillingRow).deviceName, (row as CellularBillingRow).eid, (row as CellularBillingRow).simSid,
        (row as CellularBillingRow).licenseIccid, (row as CellularBillingRow).tealBootstrapIccid, (row as CellularBillingRow).koreIccid]
      : [(row as OverlayBillingRow).hostname, (row as OverlayBillingRow).organization, (row as OverlayBillingRow).order];
    return [row.mac, row.remarks, ...fields].join(' ').toLowerCase().includes(term);
  });
}
